import { useQueryClient } from '@tanstack/react-query';
import { useLacCrawlJobsQuery } from '../queries/useLacCrawlJobsQuery';
import { useLacTenantsQuery } from '../queries/useLacTenantsQuery';
import { lacQueryKeys } from '../lacQueryKeys';
import { ALL_TENANTS_FILTER } from '../lacWebsiteCrawlsListParams';
import { useLacWebsiteCrawlsSearchParams } from './useLacWebsiteCrawlsSearchParams';

type UseLacWebsiteCrawlsTabOptions = {
  tenantName?: string;
};

export function useLacWebsiteCrawlsTab({
  tenantName,
}: UseLacWebsiteCrawlsTabOptions = {}) {
  const queryClient = useQueryClient();
  const scopedTenantName = tenantName?.trim() || undefined;
  const showTenantFilter = scopedTenantName == null;
  const tenantsQuery = useLacTenantsQuery();
  const filters = useLacWebsiteCrawlsSearchParams({
    showTenantFilter,
    tenants: tenantsQuery.data,
  });

  const filteredTenantName =
    scopedTenantName ??
    (filters.tenantFilter !== ALL_TENANTS_FILTER
      ? filters.tenantFilter
      : undefined);

  const crawlJobsQuery = useLacCrawlJobsQuery({
    startUrl: filters.debouncedStartUrl,
    tenantName: filteredTenantName,
    fromDate: filters.isoDateRange?.fromDate,
    toDate: filters.isoDateRange?.toDate,
    status: filters.filteredStatuses,
    offset: (filters.page - 1) * filters.pageSize,
    limit: filters.pageSize,
  });

  const jobs = crawlJobsQuery.data ?? [];
  const hasPreviousPage = filters.page > 1;
  const hasNextPage = jobs.length === filters.pageSize;
  const areAllExpanded =
    jobs.length > 0 &&
    jobs.every((job) => filters.expandedJobIds.has(job.crawlJobId));

  const handleManualRefresh = () => {
    void crawlJobsQuery.refetch();
    void queryClient.invalidateQueries({
      queryKey: lacQueryKeys.crawlJobTimeSeriesRoot(),
    });
    void queryClient.invalidateQueries({
      queryKey: lacQueryKeys.crawlJobAnalyticsRoot(),
    });
  };

  const expandAllJobs = () => {
    filters.setExpandedJobIds(new Set(jobs.map((job) => job.crawlJobId)));
  };

  const collapseAllJobs = () => {
    filters.setExpandedJobIds(new Set());
  };

  const toggleJobExpanded = (crawlJobId: string) => {
    filters.setExpandedJobIds((current) => {
      const next = new Set(current);
      if (next.has(crawlJobId)) {
        next.delete(crawlJobId);
      } else {
        next.add(crawlJobId);
      }
      return next;
    });
  };

  return {
    scopedTenantName,
    showTenantFilter,
    tenantsQuery,
    filters,
    filteredTenantName,
    crawlJobsQuery,
    jobs,
    hasPreviousPage,
    hasNextPage,
    areAllExpanded,
    handleManualRefresh,
    expandAllJobs,
    collapseAllJobs,
    toggleJobExpanded,
  };
}
